import * as angular from 'angular';
import * as _ from 'lodash';

import { GroupChangeBase, ICentralActions } from '../groupChangeBase';
import { guidEmpty } from '../../../common';
import { Difference, SeriesSelectorViewModel, Identifier, FlatGroup, ComponentDefinition, DigitalSet } from '../../../models';
import { IVariableCascadingService, ISeriesSelectorService, IIdentifierVmService, ITemplateService, IDigitalSetService } from '../../../services';

interface IdentifierDifference extends Difference {
    copyLabel: string;
    label: string;
    originalLabel: string;
}

class GroupChangesIdentifiers extends GroupChangeBase implements angular.IComponentController {
    allSeries: angular.IPromise<SeriesSelectorViewModel[]>;
    digitalSets: angular.IPromise<DigitalSet[]>;
    display: any[];
    tuple: any;
    variables: angular.IPromise<any>;

    private readonly _missingIdentifier: string;
    private readonly _unknown: string;

    static $inject = [
        '$translate',
        'variableCascadingService',
        'seriesSelectorService',
        'identifierVmService',
        'templateService',
        'digitalSetService'
    ];

    constructor(
        private $translate: angular.translate.ITranslateService,
        private variableCascadingService: IVariableCascadingService,
        private seriesSelectorService: ISeriesSelectorService,
        private identifierVmService: IIdentifierVmService,
        private templateService: ITemplateService,
        private _digitalSetService: IDigitalSetService
    ) {
        super();
        this._missingIdentifier = $translate.instant('missing identifier');
        this._unknown = $translate.instant('Unknown');
    }

    $onInit() {

        this.refresh();

        if (this.master) {
            const self = this;
            this.share.diff.applyAllFromTemplate = () => this.applyAllFromTemplate(self);
            this.share.diff.applyAllFromGroup = () => this.applyAllFromGroup(self);
            this.share.diff.ignoreAll = () => this.ignoreAll(self);
        }
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['groups'] || changesObj['templates'] || changesObj['changes']) {
            this.refresh();
        }
    }

    refresh() {
        this.variables = this.variableCascadingService.getPromise();
        this.allSeries = this.seriesSelectorService.getSeriesSelectorViewModels(false);
        this.digitalSets = this._digitalSetService.getAll();

        this.display = [];

        if (this.groups && this.templates && this.changes) {
            this.groups.forEach(group => {
                const template = _.find(this.templates, t => group.templateGuid === t.guid);
                if (template) {
                    this.display.push(this.buildViewGroup(group, template));
                }
            });
            this.createAlignButtons();
        }
    }


    private createAlignButtons() {
        const self = this;
        const usageToFind = {
            templates: [] as string[],
            groups: [] as string[]
        };
        this.share.central = [];
        this.share.usages = [];

        this.display.forEach(g => {

            this.share.central.push({});

            g.identifiers.forEach(i => {
                if (i.missGroup || i.missTemplate) {

                    const usageGuid = i.missGroup ? i.templateGuid : i.groupGuid;
                    this.share.usages[usageGuid] = [];
                    (i.missGroup ? usageToFind.templates : usageToFind.groups).push(usageGuid);

                    this.share.central.push(this.getCentalActions(self, i, usageGuid));
                } else {
                    this.share.central.push({});
                }

                if (i.groupChanges) {
                    const hasDifferentType = _.some(i.groupChanges, (c: any) => c.property === 'Type');
                    i.groupChanges.forEach(change => {
                        const actions: ICentralActions = this.getChangeCentralAction(self, change, hasDifferentType);
                        this.share.central.push(actions);
                    });
                }
            });
        });

        this.findUsages(usageToFind);
    }

    private getChangeCentralAction(self: this, change: any, hasDifferentType: boolean) {
        const actions: ICentralActions = {
            leftAction: () => self.share.diff.applyFromGroup(change),
            leftSelected: () => self.share.selectedDiff.fromGroup[change.guid],
            ignoreAction: () => self.share.diff.ignore(change),
            ignoreSelected: () => self.share.selectedDiff.ignores[change.guid],
            rightAction: () => self.share.diff.applyFromTemplate(change),
            rightSelected: () => self.share.selectedDiff.fromTemplate[change.guid]
        };
        if (hasDifferentType) {
            actions.leftAction = undefined;
            actions.leftSelected = undefined;
            actions.leftDelete = undefined;
            actions.rightAction = undefined;
            actions.rightDelete = undefined;
            actions.rightSelected = undefined;
            change.disableFromGroup = true;
            change.disableFromTemplate = true;
        }
        return actions;
    }

    private getCentalActions(self: this, i: any, usageGuid: any): ICentralActions {
        return {
            leftAction: () => self.share.diff.applyFromGroup(i.change),
            leftDelete: !(i.missTemplate && i.changeGuid),
            leftSelected: () => self.share.selectedDiff.fromGroup[i.change.guid],
            ignoreAction: () => self.share.diff.ignore(i.change),
            ignoreSelected: () => self.share.selectedDiff.ignores[i.change.guid],
            rightAction: () => self.share.diff.applyFromTemplate(i.change),
            rightDelete: !(i.missGroup && i.changeGuid),
            rightSelected: () => self.share.selectedDiff.fromTemplate[i.change.guid],
            usageGuid: usageGuid
        };
    }

    private setLabel(change: IdentifierDifference, original: Identifier, copy: Identifier) {
        switch (change.property) {
            case 'TagDefinitionGuid':
                this.setTagLabels(change);
                break;
            case 'TemplateGuid':
                this.setTemplateLabels(change, copy);
                break;
            case 'VariableTypeGuid':
                this.setVariableTypeLabels(change, original, copy);
                break;
            case 'VariableSubTypeGuid':
                this.setVariableSubTypeLabels(change, original, copy);
                break;
            case 'UnitGuid':
                this.setUnitLabels(change, original, copy);
                break;
            case 'QualifierGuid':
                this.setQualifierLabels(change, original, copy);
                break;
            case 'linkedGuid':
                this.setLinkedLabels(change);
                break;
            case 'DefaultValue':
                change.label = 'Default value';
                break;
            case 'SelectionStrategy':
                break;
            case 'AggregationMode':
                change.label = 'Aggregation mode';
                break;
            case 'DataTyp':
                change.label = 'Data type';
                break;
            case 'DigitalSetGuid':
                this.setDigitalSetLabels(change);
                break;
            default:
                break;
        }
    }

    private setDigitalSetLabels(change: IdentifierDifference) {
        change.label = 'Digital set';
        this.digitalSets
            .then(sets => {
                let found = _.find(sets, s => s.guid === change.originalValue);
                change.originalLabel = found ? found.name : '';
                found = _.find(sets, s => s.guid === change.copyValue);
                change.copyLabel = found ? found.name : '';
            });
    }

    private setLinkedLabels(change: IdentifierDifference) {
        change.label = 'Linked identifier';
        this.allSeries.then(series => {
            let found = _.find(series, s => s.guid === change.originalValue);
            change.originalLabel = found ? found.name : this._unknown;
            found = _.find(series, s => s.guid === change.copyValue);
            change.copyLabel = found ? found.name : this._unknown;
        });
    }

    private setQualifierLabels(change: IdentifierDifference, original: Identifier, copy: Identifier) {
        change.label = 'Qualifier';
        this.variables.then(() => {
            change.originalLabel = original.qualifierGuid === guidEmpty ? '' :
                this.variableCascadingService.getVariableQualifierName(original.variableTypeGuid, original.qualifierGuid);
            change.copyLabel = copy.qualifierGuid === guidEmpty ? '' :
                this.variableCascadingService.getVariableQualifierName(copy.variableTypeGuid, copy.qualifierGuid);
        });
    }

    private setUnitLabels(change: IdentifierDifference, original: Identifier, copy: Identifier) {
        change.label = 'Unit';
        this.variables.then(() => {
            change.originalLabel = original.unitGuid === guidEmpty ? '' :
                this.variableCascadingService.getVariableUnitName(original.variableTypeGuid, original.unitGuid);
            change.copyLabel = copy.unitGuid === guidEmpty ? ' ' :
                this.variableCascadingService.getVariableUnitName(copy.variableTypeGuid, copy.unitGuid);
        });
    }

    private setVariableSubTypeLabels(change: IdentifierDifference, original: Identifier, copy: Identifier) {
        change.label = 'Subtype';
        this.variables.then(() => {
            change.originalLabel = original.variableSubTypeGuid === guidEmpty ? ''
                : this.variableCascadingService.getVariableSubTypeName(original.variableTypeGuid, original.variableSubTypeGuid);
            change.copyLabel = copy.variableSubTypeGuid === guidEmpty ? ''
                : this.variableCascadingService.getVariableSubTypeName(copy.variableTypeGuid, copy.variableSubTypeGuid);
        });
    }

    private setVariableTypeLabels(change: IdentifierDifference, original: Identifier, copy: Identifier) {
        change.label = 'Variable type';
        this.variables.then(() => {
            change.originalLabel = original.variableTypeGuid === guidEmpty ? ''
                : this.variableCascadingService.getVariable(original.variableTypeGuid).name;
            change.copyLabel = copy.variableTypeGuid === guidEmpty ? ''
                : this.variableCascadingService.getVariable(copy.variableTypeGuid).name;
        });
    }

    private setTemplateLabels(change: IdentifierDifference, copy: Identifier) {
        (<any>change).hideLabel = true;
        change.label = 'Template link';
        change.originalLabel = `${this.$translate.instant(change.label)} : ${copy.name}`;
        change.copyLabel = this.$translate.instant('Template link is missing');
        (<any>change).copyVirtual = true;
    }

    private setTagLabels(change: IdentifierDifference) {
        change.label = 'Tag';
        this.allSeries.then(series => {
            let found = _.find(series, s => s.guid === change.originalValue);
            change.originalLabel = found ? found.name : this._unknown;
            found = _.find(series, s => s.guid === change.copyValue);
            change.copyLabel = found ? found.name : this._unknown;
        });
    }

    private buildViewGroup(group: FlatGroup, template: FlatGroup): any {
        const view = {
            guid: group.guid,
            groupName: group.name,
            templateName: template.name,
            identifiers: [],
            depth: (<any>group).depth
        };

        const groupIdentifiers = group.identifierCollection.filter(g => true);

        template.identifierCollection.forEach(i => {
            const index = _.findIndex(groupIdentifiers, idt => idt.templateGuid === i.guid || (idt.templateGuid === guidEmpty && idt.name === i.name));
            if (index >= 0) {
                const groupIdentifier = groupIdentifiers[index];
                groupIdentifiers.splice(index, 1);
                view.identifiers.push(this.buildViewIdentifier(groupIdentifier, i));
            } else {
                view.identifiers.push(this.buildMissingGroupViewIdentifier(i));
            }
        });

        // missing identifier in template
        if (groupIdentifiers.length > 0) {
            groupIdentifiers.forEach(i => {
                view.identifiers.push(this.buildMissingTemplateViewIdentifier(i));
            });
        }

        return view;
    }

    private buildViewIdentifier(identifierGroup: Identifier, identifierTemplate: Identifier): any {
        const groupDiffs = this.getChangesCollection(identifierGroup.guid);
        const templateDiffs = this.getChangesCollection(identifierTemplate.guid);

        const view = {
            guid: identifierGroup.guid,
            groupName: identifierGroup.name,
            groupGuid: identifierGroup.guid,
            groupChanges: groupDiffs,
            templateName: identifierTemplate.name,
            templateGuid: identifierTemplate.guid,
            templateChanges: templateDiffs,
            identifierType: identifierTemplate.type
        };

        groupDiffs.forEach(d => this.setLabel(d as IdentifierDifference, identifierTemplate, identifierGroup));
        templateDiffs.forEach(d => this.setLabel(d as IdentifierDifference, identifierTemplate, identifierGroup));

        return view;
    }

    private getChangesCollection(guid: string): Difference[] {
        const changes = _.find(this.changes, c => c.objectGuid === guid);
        return changes ? changes.differenceCollection : [];
    }

    private buildMissingGroupViewIdentifier(identifier: Identifier): any {
        const change = _.find(this.getChangesCollection(identifier.guid),
            c => c['property'] === 'IdentifierCollection' && c['originalValue'] === identifier.guid);
        if (change) {
            (change as any).missGroup = true;
        }

        return {
            guid: identifier.guid,
            groupName: this._missingIdentifier,
            groupGuid: undefined,
            groupChanges: [],
            templateName: identifier.name,
            templateGuid: identifier.guid,
            templateChanges: [],
            missGroup: true,
            change: change,
            changeGuid: change ? change.guid : undefined,
            identifierType: identifier.type
        };
    }

    private buildMissingTemplateViewIdentifier(identifier: Identifier) {
        const change = _.find(this.getChangesCollection(identifier.guid),
            c => c['property'] === 'IdentifierCollection' && c['copyValue'] === identifier.guid);
        if (change) {
            (change as any).missTemplate = true;
        }
        return {
            guid: identifier.guid,
            groupName: identifier.name,
            groupGuid: identifier.guid,
            groupChanges: [],
            templateName: this._missingIdentifier,
            templateGuid: undefined,
            templateChanges: [],
            missTemplate: true,
            change: change,
            changeGuid: change ? change.guid : undefined,
            identifierType: identifier.type
        };
    }

    private applyAllFromTemplate(self: this) {

        _.flatten(self.display.map(d => d.identifiers)).forEach(i => {

            if ((i.missGroup || i.missTemplate) && !this.isTreated(i.changeGuid)) {
                self.share.diff.applyFromTemplate(i.change);
            }

            i.groupChanges.forEach(c => {
                if (!this.isTreated(c.guid) && !c.disableFromTemplate) {
                    self.share.diff.applyFromTemplate(c);
                }
            });
        });
    }

    private applyAllFromGroup(self: this) {

        _.flatten(self.display.map(d => d.identifiers)).forEach(i => {

            if ((i.missGroup || i.missTemplate) && !this.isTreated(i.changeGuid)) {
                self.share.diff.applyFromGroup(i.change);
            }

            i.groupChanges.forEach(c => {
                if (!this.isTreated(c.guid) && !c.disableFromGroup) {
                    self.share.diff.applyFromGroup(c);
                }
            });
        });
    }

    private ignoreAll(self: this) {
        _.flatten(self.display.map(d => d.identifiers)).forEach(i => {
            if ((i.missGroup || i.missTemplate) && !this.isTreated(i.changeGuid)) {
                self.share.diff.ignore(i.change);
            }
            i.groupChanges.forEach(c => {
                if (!this.isTreated(c.guid)) {
                    self.share.diff.ignore(c);
                }
            });
        });
    }

    private findUsages(usageToFind: { templates: string[]; groups: string[] }) {
        if (usageToFind.templates.length > 0) {
            this.findTemplateUsages(usageToFind);
        }

        if (usageToFind.groups.length > 0) {
            this.findGroupUsages(usageToFind);
        }
    }

    private findGroupUsages(usageToFind: { templates: string[]; groups: string[]; }) {
        this.identifierVmService.getAllViewModels()
            .then(data => {
                usageToFind.groups.forEach(guid => {
                    const found: any = _.find(data, i => i.guid === guid);
                    if (found.users && found.users.length > 0 ||
                        found.precedentGroupList && found.precedentGroupList.length > 0) {
                        this.share.usages[guid] = found;
                    } else {
                        delete this.share.usages[guid];
                    }
                });
            });
    }

    private findTemplateUsages(usageToFind: { templates: string[]; groups: string[]; }) {
        this.templateService.getUsages(this.templates[0].guid)
            .then(data => {
                usageToFind.templates.forEach(guid => {
                    if (data[guid] && !data[guid].canDelete) {
                        data[guid].template = true;
                        this.share.usages[guid] = data[guid];
                    } else {
                        delete this.share.usages[guid];
                    }
                });
            });
    }
}

export const wtGroupChangesIdentifiersWidget: ComponentDefinition = {
    componentId: 'wtGroupChangesIdentifiers',
    controller: GroupChangesIdentifiers,
    controllerAs: 'vm',
    template: require('./groupChangesIdentifiers.component.html'),
    bindings: {
        changes: '<',
        groups: '<',
        templates: '<',
        master: '=?',
        share: '=',
        tuple: '<'
    }
};
